'use client'

/**
 * TourEngine — multi-step walkthrough orchestrator.
 *
 * Holds a single "active tour" at a time (no nesting). Renders the
 * SpotlightOverlay for the current step and exposes navigation functions to
 * consumers via React context.
 */

import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import { SpotlightOverlay } from './SpotlightOverlay'
import { PrereqChecklist } from './PrereqChecklist'

const TourContext = createContext(null)

const RESUME_KEY = 'ashraya:resume'
const RESUME_INTENT_KEY = 'ashraya:resumeIntent'

export function TourEngineProvider({ theme, onEvent, userRole, children }) {
  const [tour, setTour] = useState(null)        // { id, title, steps[] } | null
  const [stepIndex, setStepIndex] = useState(0)
  const [pendingTour, setPendingTour] = useState(null)
  const [pendingTicks, setPendingTicks] = useState(null)

  const emit = useCallback((evt) => {
    if (typeof onEvent === 'function') {
      try { onEvent(evt) } catch {}
    }
    if (typeof window !== 'undefined') {
      try { window.dispatchEvent(new CustomEvent('ashraya:tour', { detail: evt })) } catch {}
    }
  }, [onEvent])

  const activateTour = useCallback((t) => {
    if (!t || !Array.isArray(t.steps) || t.steps.length === 0) return

    const role = userRole || 'guest'
    const filtered = t.steps.filter((s) => {
      if (!s || !Array.isArray(s.audience_roles) || s.audience_roles.length === 0) return true
      return s.audience_roles.includes(role)
    })

    if (filtered.length === 0) return

    const renumbered = filtered.map((s, i) => ({ ...s, step_number: i + 1 }))
    const tourToRun = { ...t, steps: renumbered }

    setTour(tourToRun)
    setStepIndex(0)
    const targetIds = renumbered.map((s) => s?.target_id).filter(Boolean)
    emit({
      type: 'tour_started',
      tour_id: tourToRun.id,
      total_steps: renumbered.length,
      target_ids: targetIds,
    })
  }, [emit, userRole])

  const startTour = useCallback((t) => {
    if (!t || !Array.isArray(t.steps) || t.steps.length === 0) return

    const hasPrereqs = Array.isArray(t.prerequisites) && t.prerequisites.length > 0
    if (hasPrereqs) {
      setPendingTour(t)
      emit({
        type: 'tour_prereqs_shown',
        tour_id: t.id,
        prereq_count: t.prerequisites.length,
      })
      return
    }

    activateTour(t)
  }, [emit, activateTour])

  const confirmPrereqs = useCallback(() => {
    if (!pendingTour) return
    emit({
      type: 'tour_prereqs_confirmed',
      tour_id: pendingTour.id,
    })
    const t = pendingTour
    setPendingTour(null)
    setPendingTicks(null)
    activateTour(t)
  }, [pendingTour, emit, activateTour])

  const cancelPrereqs = useCallback(() => {
    if (!pendingTour) return
    emit({
      type: 'tour_prereqs_cancelled',
      tour_id: pendingTour.id,
    })
    setPendingTour(null)
    setPendingTicks(null)
  }, [pendingTour, emit])

  const takeMeThere = useCallback(({ href, tickedNow }) => {
    if (!pendingTour || typeof window === 'undefined') return
    try {
      const payload = {
        tour: pendingTour,
        tickedIndexes: Array.isArray(tickedNow) ? tickedNow.map(Boolean) : [],
        returnPath: window.location.pathname + window.location.search,
        timestamp: Date.now(),
      }
      sessionStorage.setItem(RESUME_KEY, JSON.stringify(payload))
    } catch {}
    emit({
      type: 'tour_prereqs_sidetrip',
      tour_id: pendingTour.id,
      destination: href,
    })
    window.location.assign(href)
  }, [pendingTour, emit])

  useEffect(() => {
    if (typeof window === 'undefined') return
    let intent = null
    let payload = null
    try {
      intent = sessionStorage.getItem(RESUME_INTENT_KEY)
      const raw = sessionStorage.getItem(RESUME_KEY)
      if (raw) payload = JSON.parse(raw)
    } catch {}
    if (intent !== '1' || !payload?.tour) return
    try {
      sessionStorage.removeItem(RESUME_INTENT_KEY)
      sessionStorage.removeItem(RESUME_KEY)
    } catch {}
    setPendingTicks(payload.tickedIndexes || [])
    setPendingTour(payload.tour)
    emit({
      type: 'tour_prereqs_resumed',
      tour_id: payload.tour.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!tour) return
    const cur = tour.steps[stepIndex]
    if (!cur) return
    emit({
      type: 'step_started',
      tour_id: tour.id,
      step_number: cur.step_number,
      target_id: cur.target_id,
    })
  }, [tour, stepIndex, emit])

  const stopTour = useCallback(() => {
    if (tour) {
      emit({ type: 'tour_abandoned', tour_id: tour.id, step_number: stepIndex + 1 })
    } else if (pendingTour) {
      emit({ type: 'tour_prereqs_cancelled', tour_id: pendingTour.id })
    }
    setTour(null)
    setStepIndex(0)
    setPendingTour(null)
  }, [tour, pendingTour, stepIndex, emit])

  const next = useCallback(() => {
    if (!tour) return
    const cur = tour.steps[stepIndex]
    emit({
      type: 'step_completed',
      tour_id: tour.id,
      step_number: cur?.step_number,
      target_id: cur?.target_id,
    })
    if (stepIndex + 1 >= tour.steps.length) {
      emit({ type: 'tour_completed', tour_id: tour.id })
      setTour(null)
      setStepIndex(0)
    } else {
      setStepIndex((i) => i + 1)
    }
  }, [tour, stepIndex, emit])

  const prev = useCallback(() => {
    if (!tour) return
    setStepIndex((i) => Math.max(0, i - 1))
  }, [tour])

  const skip = useCallback(() => {
    if (!tour) return
    const cur = tour.steps[stepIndex]
    emit({
      type: 'step_skipped',
      tour_id: tour.id,
      step_number: cur?.step_number,
      target_id: cur?.target_id,
    })
    if (stepIndex + 1 >= tour.steps.length) {
      setTour(null)
      setStepIndex(0)
    } else {
      setStepIndex((i) => i + 1)
    }
  }, [tour, stepIndex, emit])

  const onTargetMissing = useCallback((step) => {
    if (!tour || !step) return
    emit({
      type: 'target_missing',
      tour_id: tour.id,
      step_number: step.step_number,
      target_id: step.target_id,
    })
  }, [tour, emit])

  const currentStep = tour ? tour.steps[stepIndex] : null
  const isActive = !!tour || !!pendingTour
  const progress = tour ? { current: stepIndex + 1, total: tour.steps.length } : null

  const value = useMemo(
    () => ({ startTour, stopTour, next, prev, skip, currentStep, progress, isActive }),
    [startTour, stopTour, next, prev, skip, currentStep, progress, isActive]
  )

  return (
    <TourContext.Provider value={value}>
      {children}
      {pendingTour && (
        <PrereqChecklist
          tour={pendingTour}
          theme={theme}
          onConfirm={confirmPrereqs}
          onCancel={cancelPrereqs}
          onTakeMeThere={takeMeThere}
          initialTicks={pendingTicks}
        />
      )}
      {currentStep && tour && (
        <SpotlightOverlay
          step={currentStep}
          totalSteps={tour.steps.length}
          onAdvance={next}
          onSkip={skip}
          onPrev={stepIndex > 0 ? prev : null}
          onStop={stopTour}
          theme={theme}
          onTargetMissing={onTargetMissing}
        />
      )}
    </TourContext.Provider>
  )
}

export function useTourEngine() {
  const ctx = useContext(TourContext)
  if (!ctx) {
    return {
      startTour: () => {},
      stopTour: () => {},
      next: () => {},
      prev: () => {},
      skip: () => {},
      currentStep: null,
      progress: null,
      isActive: false,
    }
  }
  return ctx
}
